/**
 * Tenant Settings API client.
 */

import apiClient, { type RequestConfig } from "./client";
import type { ThemeBrandOverride, ThemeId } from "@/theme/types";

export interface AIRoleConfig {
  provider: string;
  model_id: string;
}

export interface TenantAIRolesConfig {
  workhorse?: AIRoleConfig | null;
  judge?: AIRoleConfig | null;
  fast?: AIRoleConfig | null;
  reranker?: AIRoleConfig | null;
  search_planner?: AIRoleConfig | null;
  search_expander?: AIRoleConfig | null;
  search_reranker?: AIRoleConfig | null;
}

export interface TenantUiThemeSettings {
  default_theme_id?: ThemeId | null;
  allow_user_override?: boolean;
  brand?: ThemeBrandOverride | null;
}

export type ProductProfileId =
  | "eca_accelerator"
  | "eca_lite"
  | "defense_video"
  | "full_review";

export type PublicHomeMode = "marketing" | "hybrid" | "app";

export interface ProductProfileMarketingConfig {
  hero: string;
  subhead: string;
  primary_cta: string;
  secondary_cta?: string | null;
  proof_points: string[];
}

export interface ProductProfilePricingPlan {
  id: string;
  label: string;
  price_label: string;
  summary: string;
  cta_label: string;
}

export interface ProductProfilePricingConfig {
  headline: string;
  subhead: string;
  plans: ProductProfilePricingPlan[];
  footnote?: string | null;
}

export interface ProductProfileNavConfig {
  default_home_route: string;
  primary_items: string[];
  hidden_items: string[];
}

export interface ProductProfileWorkflowConfig {
  show_preflight: boolean;
  show_unlock: boolean;
  show_search: boolean;
  show_sweep: boolean;
  show_chronology: boolean;
  show_review: boolean;
  show_exports: boolean;
  show_queue: boolean;
  show_help: boolean;
  show_assistant: boolean;
  show_portfolio: boolean;
  show_my_work: boolean;
  show_case_tools: boolean;
  show_clustering: boolean;
  show_review_center: boolean;
  show_redactions: boolean;
}

export interface ProductProfileConfig {
  id: ProductProfileId;
  label: string;
  description: string;
  public_home_mode: PublicHomeMode;
  marketing: ProductProfileMarketingConfig;
  pricing: ProductProfilePricingConfig;
  nav: ProductProfileNavConfig;
  workflow: ProductProfileWorkflowConfig;
}

export interface PublicProductProfileSummary {
  id: ProductProfileId;
  label: string;
  description: string;
  public_home_mode: PublicHomeMode;
}

export interface TenantSettings {
  ai_guidance_enabled: boolean;
  enforce_assignment_ownership: boolean;
  ai_suggestions_enabled?: boolean;
  ai_roles?: TenantAIRolesConfig | null;
  ui_theme?: TenantUiThemeSettings | null;
  product_profile: ProductProfileConfig;
}

export interface PublicTenantBootstrap {
  host?: string | null;
  tenant_slug?: string | null;
  ui_theme?: TenantUiThemeSettings | null;
  product_profile: PublicProductProfileSummary;
}

const ENDPOINT = "/tenant/settings";
const ADMIN_ENDPOINT = "/admin/tenant/settings";
const PUBLIC_BOOTSTRAP_ENDPOINT = "/public/tenant-bootstrap";

export const tenantSettingsApi = {
  async getSettings(config?: RequestConfig): Promise<TenantSettings> {
    const { data } = await apiClient.get<TenantSettings>(ENDPOINT, config);
    return data;
  },

  async getPublicBootstrap(
    host?: string,
    config?: RequestConfig
  ): Promise<PublicTenantBootstrap> {
    const { data } = await apiClient.get<PublicTenantBootstrap>(
      PUBLIC_BOOTSTRAP_ENDPOINT,
      {
        ...config,
        skipAuth: true,
        params: {
          ...config?.params,
          ...(host ? { host } : {}),
        },
      }
    );
    return data;
  },

  async updateSettings(
    body: Partial<TenantSettings>,
    config?: RequestConfig
  ): Promise<TenantSettings> {
    const { data } = await apiClient.patch<TenantSettings>(
      ADMIN_ENDPOINT,
      body,
      config
    );
    return data;
  },
};

export interface RetentionPolicy {
  archive_to_cold_days: number;
  cold_to_purge_eligible_days: number;
  auto_cold_enabled: boolean;
  auto_purge_enabled: boolean;
  purge_requires_legal_review: boolean;
  retain_extracted_text_in_cold: boolean;
  retain_native_files_in_cold: boolean;
}

export const retentionPolicyApi = {
  async get(config?: RequestConfig): Promise<RetentionPolicy> {
    const { data } = await apiClient.get<RetentionPolicy>("/tenant/retention-policy", config);
    return data;
  },

  async update(body: Partial<RetentionPolicy>, config?: RequestConfig): Promise<RetentionPolicy> {
    const { data } = await apiClient.patch<RetentionPolicy>("/admin/tenant/retention-policy", body, config);
    return data;
  },
};

export default tenantSettingsApi;
